import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton, { type IconButtonProps } from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import type { RowAction } from "_shared/_views/tables/row-actions";
import type { DownloadVerdictFields } from "documents/downloads/document-download-verdict";
import { documentDownloadWithheldReason } from "documents/downloads/document-download-withholding";
import type { ReactNode } from "react";

/**
 * A document's name, where the download is withheld: the same text, not offered as a link. Focusable,
 * because the tooltip that says why opens on focus, and a keyboard reaches it no other way.
 */
const WithheldName = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: "not-allowed",
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type DocumentDownloadControlProps = {
  /**
   * This reader's verdict: the documents API's `Document`, or for a work or proposal row what
   * `useDocumentDownloadVerdicts` found for it. Undefined while it is still being read, which leaves
   * Download enabled and the server to decide.
   */
  document: DownloadVerdictFields | undefined;
  /** The document's name, for the accessible name and the tooltip. */
  name: string;
  className?: string;
  onDownload: () => void;
  /** A download of this document is under way. */
  downloading?: boolean;
  /** Disabled for a reason of the page's own, such as another download under way. */
  disabled?: boolean;
  /** What the tooltip says while `disabled` and nothing withholds the download. */
  disabledReason?: string;
  size?: IconButtonProps["size"];
  edge?: IconButtonProps["edge"];
  iconFontSize?: "small" | "medium";
};

/**
 * Download, disabled with the reason when the server would refuse it (#6992).
 *
 * Every page that downloads a document uses this, or `documentDownloadRowAction` where the row's
 * actions are a menu, or `DocumentDownloadName` where the name is the download. They read one rule,
 * `documentDownloadWithheldReason`, so a page cannot offer a download another page withholds.
 */
export default function DocumentDownloadControl(props: Readonly<DocumentDownloadControlProps>) {
  const withheldReason = documentDownloadWithheldReason(props.document);
  const pageDisabled = props.disabled ?? false;
  const isDisabled = Boolean(withheldReason) || pageDisabled;
  const pageReason = pageDisabled ? props.disabledReason : undefined;
  const label = `Download ${props.name}`;
  const reason = withheldReason ?? pageReason;
  const icon = props.downloading ? (
    <CircularProgress color="primary" size={props.iconFontSize === "small" ? 18 : 24} disableShrink thickness={4} />
  ) : (
    <DownloadIcon color={isDisabled ? "disabled" : "primary"} fontSize={props.iconFontSize ?? "medium"} />
  );

  // The page's own reason - another download under way - lasts a moment, so the button is simply
  // disabled, and the tooltip hangs from a span because a disabled button fires no events.
  if (pageDisabled) {
    return (
      <Tooltip describeChild title={reason ?? label}>
        <span>
          <IconButton aria-label={label} className={props.className} disabled edge={props.edge} size={props.size}>
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // A withheld download stays focusable and says it is unavailable, rather than being disabled: a
  // disabled button cannot be focused, and then a keyboard never reaches the reason, which on a
  // proposal's policies is said nowhere else. describeChild keeps "Download <name>" as the name and
  // makes the reason its description.
  return (
    <Tooltip describeChild title={reason ?? label}>
      <IconButton
        aria-label={label}
        aria-disabled={withheldReason ? true : undefined}
        className={props.className}
        disableRipple={Boolean(withheldReason)}
        edge={props.edge}
        onClick={withheldReason ? undefined : props.onDownload}
        size={props.size}
        sx={withheldReason ? { cursor: "not-allowed" } : undefined}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

type DocumentDownloadNameProps = {
  document: DownloadVerdictFields | undefined;
  /** The name as the row draws it, search highlighting included. */
  children: ReactNode;
  /** Renders the name as the row's link to the download. */
  renderLink: (children: ReactNode) => ReactNode;
};

/**
 * A document's name that is its download: the row's link, or plain text saying why on hover and
 * focus when the server would refuse the download (#6992).
 */
export function DocumentDownloadName(props: Readonly<DocumentDownloadNameProps>) {
  const withheldReason = documentDownloadWithheldReason(props.document);
  if (!withheldReason) return <>{props.renderLink(props.children)}</>;

  return (
    <Tooltip describeChild title={withheldReason}>
      <WithheldName aria-disabled tabIndex={0}>
        {props.children}
      </WithheldName>
    </Tooltip>
  );
}

/** The Download entry of a row's actions, disabled with the reason when the server would refuse it. */
export function documentDownloadRowAction(
  document: DownloadVerdictFields | undefined,
  action: Omit<RowAction, "key" | "label" | "icon" | "disabled" | "disabledReason"> & {
    disabled?: boolean;
    disabledReason?: string;
  },
): RowAction {
  const withheldReason = documentDownloadWithheldReason(document);
  return {
    key: "download",
    label: "Download",
    icon: <DownloadIcon />,
    ...action,
    disabled: Boolean(withheldReason) || (action.disabled ?? false),
    disabledReason: withheldReason ?? action.disabledReason,
  };
}
